import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  FunnelChart,
  Funnel,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { vendorService } from '../services/vendorService';
import { referralService } from '../services/referralService';
import { commissionService } from '../services/commissionService';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface DashboardStats {
  totalVendors: number;
  activeVendors: number;
  totalReferrals: number;
  referralsThisMonth: number;
  referralsLastMonth: number;
  avgConversionRate: number;
  totalCommissions: number;
  pendingCommissions: number;
  paidCommissions: number;
}

const VendorDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalVendors: 0,
    activeVendors: 0,
    totalReferrals: 0,
    referralsThisMonth: 0,
    referralsLastMonth: 0,
    avgConversionRate: 0,
    totalCommissions: 0,
    pendingCommissions: 0,
    paidCommissions: 0,
  });
  const [funnelData, setFunnelData] = useState<any[]>([]);
  const [topVendors, setTopVendors] = useState<any[]>([]);
  const [commissionTrends, setCommissionTrends] = useState<any[]>([]);
  const [statusDistribution, setStatusDistribution] = useState<any[]>([]);
  const [cityPerformance, setCityPerformance] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState('30');

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch vendors
      const vendorsResponse = await vendorService.getVendors({ limit: 1000 });
      const vendors = vendorsResponse.data || [];

      // Fetch referrals
      const referralsResponse = await referralService.getReferrals({ limit: 1000 });
      const referrals = referralsResponse.data || [];

      // Fetch commissions
      const commissionsResponse = await commissionService.getCommissions({ limit: 1000 });
      const commissions = commissionsResponse.data || [];

      // Calculate stats
      const activeVendors = vendors.filter((v: any) => v.status === 'ACTIVE' || v.status === 'VERIFIED');
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      const referralsThisMonth = referrals.filter(
        (r: any) => new Date(r.leadCreatedAt) >= thisMonthStart
      );
      const referralsLastMonth = referrals.filter(
        (r: any) =>
          new Date(r.leadCreatedAt) >= lastMonthStart &&
          new Date(r.leadCreatedAt) <= lastMonthEnd
      );

      const avgConversion =
        vendors.reduce((sum: number, v: any) => sum + v.conversionRate, 0) / (vendors.length || 1);

      const pendingCommissions = commissions.filter((c: any) => c.status === 'PENDING');
      const paidCommissions = commissions.filter((c: any) => c.status === 'PAID');

      setStats({
        totalVendors: vendors.length,
        activeVendors: activeVendors.length,
        totalReferrals: referrals.length,
        referralsThisMonth: referralsThisMonth.length,
        referralsLastMonth: referralsLastMonth.length,
        avgConversionRate: avgConversion,
        totalCommissions: commissions.reduce((sum: number, c: any) => sum + c.amount, 0),
        pendingCommissions: pendingCommissions.reduce((sum: number, c: any) => sum + c.amount, 0),
        paidCommissions: paidCommissions.reduce((sum: number, c: any) => sum + c.amount, 0),
      });

      // Fetch funnel data
      try {
        const funnelResponse = await referralService.getReferralFunnel({});
        const funnel = funnelResponse.data;
        if (funnel) {
          setFunnelData([
            { name: 'Lead Created', value: funnel.leadCreated || 0, fill: '#8884d8' },
            { name: 'Contacted', value: funnel.contacted || 0, fill: '#83a6ed' },
            { name: 'Registered', value: funnel.registered || 0, fill: '#8dd1e1' },
            { name: 'KYC Verified', value: funnel.kycVerified || 0, fill: '#82ca9d' },
            { name: 'First Delivery', value: funnel.firstDelivery || 0, fill: '#a4de6c' },
            { name: 'Active', value: funnel.active || 0, fill: '#d0ed57' },
            { name: 'Retained 30', value: funnel.retained30 || 0, fill: '#ffc658' },
          ]);
        }
      } catch (error) {
        console.error('Error loading funnel:', error);
      }

      // Calculate top vendors by conversion rate
      const sortedVendors = [...vendors]
        .filter((v) => v.totalReferrals > 0)
        .sort((a, b) => b.conversionRate - a.conversionRate)
        .slice(0, 10);
      setTopVendors(
        sortedVendors.map((v) => ({
          name: v.name,
          rate: (v.conversionRate * 100).toFixed(1),
          referrals: v.totalReferrals,
        }))
      );

      // Commission trends (mock data - in real app, aggregate by month)
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
      setCommissionTrends(
        monthNames.map((month) => ({
          month,
          pending: Math.random() * 50000 + 10000,
          approved: Math.random() * 40000 + 15000,
          paid: Math.random() * 30000 + 20000,
        }))
      );

      // Status distribution
      const statusCounts = referrals.reduce((acc: any, r: any) => {
        acc[r.status] = (acc[r.status] || 0) + 1;
        return acc;
      }, {});
      setStatusDistribution(
        Object.entries(statusCounts).map(([status, count]) => ({
          name: status.replace(/_/g, ' '),
          value: count as number,
        }))
      );

      // City performance
      const cityData = referrals.reduce((acc: any, r: any) => {
        const city = r.city || 'Unknown';
        if (!acc[city]) {
          acc[city] = { total: 0, converted: 0 };
        }
        acc[city].total++;
        if (
          r.status === 'ACTIVE' ||
          r.status === 'RETAINED_30' ||
          r.status === 'RETAINED_90'
        ) {
          acc[city].converted++;
        }
        return acc;
      }, {});

      const cityPerf = Object.entries(cityData)
        .map(([city, data]: [string, any]) => ({
          city,
          referrals: data.total,
          conversions: data.converted,
          rate: ((data.converted / data.total) * 100).toFixed(1),
        }))
        .sort((a, b) => b.referrals - a.referrals)
        .slice(0, 10);
      setCityPerformance(cityPerf);
    } catch (error) {
      console.error('❌ Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const referralChange =
    stats.referralsLastMonth > 0
      ? ((stats.referralsThisMonth - stats.referralsLastMonth) / stats.referralsLastMonth) * 100
      : 0;

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export dashboard data');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Vendor Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="7">Last 7 Days</MenuItem>
              <MenuItem value="30">Last 30 Days</MenuItem>
              <MenuItem value="90">Last 90 Days</MenuItem>
              <MenuItem value="365">Last Year</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadDashboardData}>
            Refresh
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPI Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total Vendors
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.totalVendors}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats.activeVendors} active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total Referrals
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.totalReferrals}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption">This month: {stats.referralsThisMonth}</Typography>
                    {referralChange !== 0 && (
                      <Chip
                        icon={
                          referralChange > 0 ? (
                            <TrendingUpIcon fontSize="small" />
                          ) : (
                            <TrendingDownIcon fontSize="small" />
                          )
                        }
                        label={`${Math.abs(referralChange).toFixed(1)}%`}
                        size="small"
                        color={referralChange > 0 ? 'success' : 'error'}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Avg Conversion Rate
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {(stats.avgConversionRate * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Across all vendors
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total Commissions
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    ₹{(stats.totalCommissions / 1000).toFixed(1)}K
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip
                      label={`Pending: ₹${(stats.pendingCommissions / 1000).toFixed(1)}K`}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                    <Chip
                      label={`Paid: ₹${(stats.paidCommissions / 1000).toFixed(1)}K`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts Row 1 */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Referral Funnel */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Referral Pipeline Funnel
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <FunnelChart>
                    <Tooltip />
                    <Funnel dataKey="value" data={funnelData} isAnimationActive>
                      <LabelList position="right" fill="#000" stroke="none" dataKey="name" />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Top Vendors */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Vendors by Conversion Rate
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topVendors} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip />
                    <Bar dataKey="rate" fill="#82ca9d" name="Conversion Rate (%)" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Charts Row 2 */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Commission Trends */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Commission Payout Trends
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={commissionTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="pending"
                      stroke="#ffc658"
                      strokeWidth={2}
                      name="Pending (₹)"
                    />
                    <Line
                      type="monotone"
                      dataKey="approved"
                      stroke="#82ca9d"
                      strokeWidth={2}
                      name="Approved (₹)"
                    />
                    <Line
                      type="monotone"
                      dataKey="paid"
                      stroke="#8884d8"
                      strokeWidth={2}
                      name="Paid (₹)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Status Distribution */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Referral Status Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statusDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusDistribution.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* City Performance */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  City-wise Performance
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={cityPerformance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="city" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="referrals" fill="#8884d8" name="Total Referrals" />
                    <Bar dataKey="conversions" fill="#82ca9d" name="Conversions" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default VendorDashboard;
